import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';

import { Box, Typography, InputBase, Button } from '@mui/material';
import { styles } from './reset-password.styles';
import { ROUTES } from '../../../helpers/constans';
import { resetPasswordSchema } from '../../../helpers/formik-shemas/auth.schema';
import { fetchResetPassword } from '../../../services/auth.service';

interface IFormValues {
  email: string;
}

const ResetPassword = (): ReactElement => {
  const navigate = useNavigate();
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const formik = useFormik<IFormValues>({
    initialValues: { email: '' },
    validationSchema: resetPasswordSchema,
    validateOnChange: false,
    onSubmit: () => {},
  });

  const handleSendLink = async (event: any): Promise<void> => {
    event.preventDefault();
    
    const validation = await formik.validateForm();
    if (Object.keys(validation).length > 0) {
      formik.setErrors(validation);
      return;
    }
    
    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await fetchResetPassword({ email: formik.values.email });
      setMessage(response.message);
      formik.resetForm();
    } catch (error) {      
      setMessage('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = (): void => {
    navigate(ROUTES.login);
  };

  return (
    <Box sx={styles.wrapper}>
      <Box
        component="form"
        sx={styles.container}
        onSubmit={handleSendLink}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      >
        <Typography variant="h4" sx={styles.title}>
          Reset Password
        </Typography>
        <Typography sx={styles.instruction}>
          Enter your email address and we'll send you a link to reset your password.
        </Typography>
        {message && (
          <Typography sx={{ mb: 2, color: message === 'Something went wrong' ? 'error.main' : 'success.main' }}>
            {message}
          </Typography>
        )}
        <Box sx={styles.inputBox}>
          <Typography sx={styles.inputLabel}>Email</Typography>
          <InputBase
            sx={styles.input}
            name="email"
            placeholder="Email"
            value={formik.values.email}
            error={Boolean(formik.values.email) && Boolean(formik.errors.email)}
          />
          <Typography sx={styles.inputErrorLabel}>{formik.errors.email}</Typography>
        </Box>
        <Button sx={styles.button} onClick={handleSendLink} disabled={isLoading}>
          {isLoading ? 'Sending...' : 'Send Link'}
        </Button>
        <Box sx={styles.backToLoginBlock}>
          <Typography sx={styles.backToLoginTitle}>Remember your password?</Typography>
          <Box component="span" sx={styles.link} onClick={handleBackToLogin}>
            Back to Login
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;